import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, Sunset, Sunrise, Star, Settings, X, MapPin, Info, ChevronDown, ChevronUp, Check, Volume2 } from 'lucide-react';
import { autoPickMethod } from '@/utils/prayerMethodAuto';
import { pickLang, RTL_LANGS, PRAYER_NAMES, tt } from '@/utils/localizedStrings';
import {
  getTodayConfirmations,
  canConfirm,
  confirmPrayer,
} from '@/utils/prayerConfirmations';
import { TamamWelcome, TamamSession, TamamSummaryToast, TAMAM_INITIAL_RAKAT } from '@/components/TamamAlSalah';
import { TAMAM_STRINGS } from '@/utils/tamamStrings';
import AudioNotificationsCard from '@/components/AudioNotificationsCard';
import { AUDIO_STRINGS } from '@/utils/audioStrings';
import {
  getSettings as getAudioSettings,
  loadTone as loadAudioTone,
  playBlob as playAudioBlob,
  hasFired as hasPrayerAudioFired,
  markFired as markPrayerAudioFired,
} from '@/utils/audioNotifications';
import { syncPushIfNeeded, listenForegroundPush, showForegroundNotification } from '@/utils/prayerPush';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// "NEW" corner ribbon on the Tamam al-Salah entry button is displayed for
// 30 days from this launch date, then disappears on its own.
// The launch date reflects when the "Tamam al-Salah" feature became
// visible to production users on falakmoon.com.
const TAMAM_NEW_BADGE_LAUNCH_ISO = '2026-08-20'; // production launch of feature
const TAMAM_NEW_BADGE_DURATION_DAYS = 30;
const isTamamNewBadgeVisible = () => {
  const launch = new Date(TAMAM_NEW_BADGE_LAUNCH_ISO).getTime();
  const cutoff = launch + TAMAM_NEW_BADGE_DURATION_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() < cutoff;
};

// Banner i18n — multilingual hint shown at the top of the Prayer Times widget
const HINT = {
  ar: {
    title: 'موقعك محدّد بدقّة ومواقيتك مضبوطة',
    p1: 'تم اعتماد طريقة الحساب الأكثر شيوعاً في نطاقك الجغرافي.',
    p2: 'يمكنك التعديل اليدوي من زر الإعدادات (⚙):',
    li1: 'اختيار طريقة الحساب الأنسب لك',
    li2: 'تحديد المذهب الفقهي لصلاة العصر',
    li3: 'إضافة أو خصم دقائق لمطابقة المسجد الأقرب',
    expand: 'عرض التفاصيل',
    collapse: 'إخفاء التفاصيل',
  },
  en: {
    title: 'Your location is detected · Prayer times are precise',
    p1: 'Falak uses the calculation method most commonly adopted in your region.',
    p2: 'You can fine-tune everything from the settings (⚙) button:',
    li1: 'Pick the calculation method that suits you',
    li2: 'Choose the school of jurisprudence for Asr',
    li3: 'Add or subtract minutes to match your local mosque',
    expand: 'Show details',
    collapse: 'Hide details',
  },
  id: {
    title: 'Lokasi Anda terdeteksi · Waktu salat akurat',
    p1: 'Falak menggunakan metode perhitungan yang paling umum di wilayah Anda.',
    p2: 'Anda dapat menyesuaikan melalui tombol pengaturan (⚙):',
    li1: 'Pilih metode perhitungan yang paling sesuai',
    li2: 'Pilih mazhab untuk perhitungan Ashar',
    li3: 'Tambah atau kurangi menit agar cocok dengan masjid terdekat',
    expand: 'Tampilkan detail',
    collapse: 'Sembunyikan detail',
  },
  ur: {
    title: 'آپ کا مقام درست · نمازوں کے اوقات درست',
    p1: 'فلک آپ کے علاقے میں مروج حسابی طریقہ استعمال کرتا ہے۔',
    p2: 'آپ سیٹنگز (⚙) کے بٹن سے تبدیلیاں کر سکتے ہیں:',
    li1: 'اپنی پسند کا حسابی طریقہ منتخب کریں',
    li2: 'عصر کے لیے فقہی مذہب کا انتخاب',
    li3: 'قریبی مسجد سے مطابقت کے لیے منٹ جوڑیں یا گھٹائیں',
    expand: 'تفصیلات دکھائیں',
    collapse: 'تفصیلات چھپائیں',
  },
  fr: {
    title: 'Votre position est détectée · Horaires précis',
    p1: 'Falak utilise la méthode de calcul la plus répandue dans votre région.',
    p2: 'Vous pouvez ajuster via le bouton paramètres (⚙) :',
    li1: 'Choisir la méthode de calcul qui vous convient',
    li2: 'Choisir l\'école juridique pour le Asr',
    li3: 'Ajouter ou soustraire des minutes pour la mosquée la plus proche',
    expand: 'Afficher les détails',
    collapse: 'Masquer les détails',
  },
  bn: {
    title: 'আপনার অবস্থান নির্ধারিত · নামাজের সময় সঠিক',
    p1: 'ফালাক আপনার অঞ্চলের সর্বাধিক প্রচলিত পদ্ধতি ব্যবহার করছে।',
    p2: 'সেটিংস (⚙) বাটন থেকে কাস্টমাইজ করতে পারেন:',
    li1: 'আপনার পছন্দের গণনা পদ্ধতি নির্বাচন',
    li2: 'আসরের জন্য মাজহাব নির্ধারণ',
    li3: 'নিকটতম মসজিদের সাথে মেলাতে মিনিট যোগ বা বিয়োগ',
    expand: 'বিস্তারিত দেখান',
    collapse: 'বিস্তারিত লুকান',
  },
  zh: {
    title: '您的位置已检测 · 礼拜时间精准',
    p1: 'Falak 采用您所在地区最常用的计算方法。',
    p2: '您可以通过设置 (⚙) 按钮进行调整：',
    li1: '选择最适合您的计算方法',
    li2: '为晡礼选择教法学派',
    li3: '增加或减少分钟以匹配最近的清真寺',
    expand: '显示详情',
    collapse: '隐藏详情',
  },
  fa: {
    title: 'موقعیت شما با دقت تشخیص داده شده و اوقات نماز دقیق است',
    p1: 'فلک از روش محاسبه‌ی رایج در منطقه‌ی شما استفاده می‌کند.',
    p2: 'می‌توانید از دکمه‌ی تنظیمات (⚙) آن را شخصی‌سازی کنید:',
    li1: 'انتخاب روش محاسبه مناسب شما',
    li2: 'انتخاب مذهب فقهی برای نماز عصر',
    li3: 'افزودن یا کاستن دقایق برای تطابق با مسجد نزدیک',
    expand: 'نمایش جزئیات',
    collapse: 'پنهان کردن جزئیات',
  },
  tr: {
    title: 'Konumunuz hassas şekilde tespit edildi · Namaz vakitleri kesin',
    p1: 'Falak, bölgenizde en yaygın kullanılan hesaplama yöntemini kullanır.',
    p2: 'Ayarlar (⚙) düğmesinden ince ayar yapabilirsiniz:',
    li1: 'Size uygun hesaplama yöntemini seçin',
    li2: 'İkindi için fıkıh ekolünü seçin',
    li3: 'En yakın camiyle eşleştirmek için dakika ekleyin/çıkarın',
    expand: 'Ayrıntıları göster',
    collapse: 'Ayrıntıları gizle',
  },
};

// UI labels used elsewhere in this widget (replaces ad-hoc isRTL ? ar : en).
const UI = {
  prayerTimes:    { en: 'Prayer Times', ar: 'مواقيت الصلاة', id: 'Waktu Salat', ur: 'نماز کے اوقات', fr: 'Heures de Prière', bn: 'নামাজের সময়', zh: '礼拜时间', fa: 'اوقات نماز', tr: 'Namaz Vakitleri', es: 'Horarios de Oración', pt: 'Horários de Oração', de: 'Gebetszeiten', it: 'Orari di Preghiera' },
  nextPrayer:     { en: 'Next prayer', ar: 'الصلاة القادمة', id: 'Salat berikutnya', ur: 'اگلی نماز', fr: 'Prochaine prière', bn: 'পরবর্তী নামাজ', zh: '下一次礼拜', fa: 'نماز بعدی', tr: 'Sonraki namaz', es: 'Próxima oración', pt: 'Próxima oração', de: 'Nächstes Gebet', it: 'Prossima preghiera' },
  settings:       { en: 'Settings', ar: 'إعدادات', id: 'Pengaturan', ur: 'سیٹنگز', fr: 'Paramètres', bn: 'সেটিংস', zh: '设置', fa: 'تنظیمات', tr: 'Ayarlar', es: 'Ajustes', pt: 'Configurações', de: 'Einstellungen', it: 'Impostazioni' },
  prayerSettings: { en: 'Prayer settings', ar: 'إعدادات الصلاة', id: 'Pengaturan salat', ur: 'نماز کی سیٹنگز', fr: 'Paramètres de prière', bn: 'নামাজের সেটিংস', zh: '礼拜设置', fa: 'تنظیمات نماز', tr: 'Namaz ayarları', es: 'Ajustes de oración', pt: 'Configurações de oração', de: 'Gebetseinstellungen', it: 'Impostazioni preghiera' },
  prayerSettingsTitle: { en: 'Prayer settings', ar: 'إعدادات مواقيت الصلاة', id: 'Pengaturan waktu salat', ur: 'اوقاتِ نماز سیٹنگز', fr: 'Réglages des prières', bn: 'নামাজের সময় সেটিংস', zh: '礼拜时间设置', fa: 'تنظیمات اوقات نماز', tr: 'Namaz vakitleri ayarları', es: 'Ajustes de horarios de oración', pt: 'Configurações de horários de oração', de: 'Gebetszeiten-Einstellungen', it: 'Impostazioni orari di preghiera' },
  close:          { en: 'Close', ar: 'إغلاق', id: 'Tutup', ur: 'بند کریں', fr: 'Fermer', bn: 'বন্ধ', zh: '关闭', fa: 'بستن', tr: 'Kapat', es: 'Cerrar', pt: 'Fechar', de: 'Schließen', it: 'Chiudi' },
  cancel:         { en: 'Cancel', ar: 'إلغاء', id: 'Batal', ur: 'منسوخ', fr: 'Annuler', bn: 'বাতিল', zh: '取消', fa: 'انصراف', tr: 'İptal', es: 'Cancelar', pt: 'Cancelar', de: 'Abbrechen', it: 'Annulla' },
  saveNew:        { en: 'Save new settings', ar: 'حفظ الإعدادات الجديدة', id: 'Simpan pengaturan baru', ur: 'نئی سیٹنگز محفوظ کریں', fr: 'Enregistrer les nouveaux paramètres', bn: 'নতুন সেটিংস সংরক্ষণ', zh: '保存新设置', fa: 'ذخیره تنظیمات جدید', tr: 'Yeni ayarları kaydet', es: 'Guardar nuevos ajustes', pt: 'Salvar novas configurações', de: 'Neue Einstellungen speichern', it: 'Salva nuove impostazioni' },
  resetGPS:       { en: 'Return to GPS-based defaults', ar: 'العودة إلى الإعدادات الافتراضية حسب GPS', id: 'Kembali ke default berbasis GPS', ur: 'GPS بنیاد پر ڈیفالٹ پر واپس جائیں', fr: 'Revenir aux paramètres GPS par défaut', bn: 'GPS ভিত্তিক ডিফল্টে ফিরুন', zh: '恢复基于 GPS 的默认设置', fa: 'بازگشت به تنظیمات پیش‌فرض GPS', tr: 'GPS varsayılanlarına dön', es: 'Volver a valores predeterminados por GPS', pt: 'Voltar aos padrões por GPS', de: 'Zu GPS-Standardwerten zurückkehren', it: 'Torna ai valori predefiniti GPS' },
  modalIntro:     { en: 'Pick your preferences then click "Save new settings" below. Closing without saving discards changes.', ar: 'اختر إعداداتك ثم اضغط زر "حفظ الإعدادات الجديدة" أدناه. الإغلاق بدون حفظ يلغي التغييرات.', id: 'Pilih preferensi Anda lalu klik "Simpan pengaturan baru". Menutup tanpa menyimpan akan membatalkan perubahan.', ur: 'اپنی ترجیحات منتخب کریں پھر "نئی سیٹنگز محفوظ کریں" پر کلک کریں۔ بغیر محفوظ کیے بند کرنا تبدیلیاں رد کر دے گا۔', fr: 'Choisissez vos préférences puis cliquez "Enregistrer". Fermer sans enregistrer annule les changements.', bn: 'আপনার পছন্দ নির্বাচন করুন তারপর নিচে "নতুন সেটিংস সংরক্ষণ" এ ক্লিক করুন।', zh: '选择您的偏好，然后点击下方"保存新设置"。未保存关闭将丢弃更改。', fa: 'تنظیمات خود را انتخاب کرده و سپس روی "ذخیره تنظیمات جدید" کلیک کنید.', tr: 'Tercihlerinizi seçin ve aşağıdaki "Yeni ayarları kaydet"e tıklayın.', es: 'Elige tus preferencias y luego pulsa "Guardar nuevos ajustes". Cerrar sin guardar descarta los cambios.', pt: 'Escolha as preferências e clique em "Salvar novas configurações". Fechar sem salvar descarta as alterações.', de: 'Wählen Sie Ihre Präferenzen und klicken Sie unten auf „Neue Einstellungen speichern". Ohne Speichern schließen verwirft die Änderungen.', it: 'Scegli le tue preferenze e clicca "Salva nuove impostazioni". Chiudere senza salvare annulla le modifiche.' },
  calcMethod:     { en: 'Calculation method', ar: 'طريقة الحساب', id: 'Metode perhitungan', ur: 'حسابی طریقہ', fr: 'Méthode de calcul', bn: 'গণনা পদ্ধতি', zh: '计算方法', fa: 'روش محاسبه', tr: 'Hesaplama yöntemi', es: 'Método de cálculo', pt: 'Método de cálculo', de: 'Berechnungsmethode', it: 'Metodo di calcolo' },
  asrCalc:        { en: 'Asr calculation', ar: 'حساب العصر', id: 'Perhitungan Ashar', ur: 'عصر کا حساب', fr: 'Calcul du Asr', bn: 'আসরের গণনা', zh: '晡礼计算', fa: 'محاسبه عصر', tr: 'İkindi hesaplaması', es: 'Cálculo del Asr', pt: 'Cálculo do Asr', de: 'Asr-Berechnung', it: 'Calcolo dell\'Asr' },
  methodLabel:    { en: 'Method: ', ar: 'طريقة الحساب: ', id: 'Metode: ', ur: 'طریقہ: ', fr: 'Méthode : ', bn: 'পদ্ধতি: ', zh: '方法：', fa: 'روش: ', tr: 'Yöntem: ', es: 'Método: ', pt: 'Método: ', de: 'Methode: ', it: 'Metodo: ' },
  autoArrow:      { en: 'Auto → ', ar: 'تلقائي ← ', id: 'Otomatis → ', ur: 'خودکار ← ', fr: 'Auto → ', bn: 'অটো → ', zh: '自动 → ', fa: 'خودکار ← ', tr: 'Oto → ', es: 'Auto → ', pt: 'Auto → ', de: 'Auto → ', it: 'Auto → ' },
  offset:         { en: ' offset', ar: 'تعديل ', id: ' offset', ur: 'تبدیلی ', fr: ' décalage', bn: ' সমন্বয়', zh: ' 偏移', fa: 'تنظیم ', tr: ' farkı', es: ' ajuste', pt: ' ajuste', de: ' Versatz', it: ' scarto' },
  minShort:       { en: 'min', ar: 'د', id: 'mnt', ur: 'منٹ', fr: 'min', bn: 'মিনিট', zh: '分', fa: 'دق', tr: 'dk', es: 'min', pt: 'min', de: 'Min', it: 'min' },
  negHint:        { en: 'Negative values (e.g. −2) bring the prayer earlier by a few minutes.', ar: 'يمكن استخدام الأرقام السالبة (مثل −2) لتقديم الوقت بضع دقائق.', id: 'Nilai negatif (mis. −2) memajukan waktu salat beberapa menit.', ur: 'منفی اقدار (جیسے −2) نماز کو کچھ منٹ پہلے لاتے ہیں۔', fr: 'Les valeurs négatives (ex. −2) avancent la prière de quelques minutes.', bn: 'নেতিবাচক মান (যেমন −2) নামাজকে কয়েক মিনিট এগিয়ে আনে।', zh: '负值（如 −2）将礼拜时间提前几分钟。', fa: 'اعداد منفی (مانند −2) نماز را چند دقیقه جلوتر می‌اندازد.', tr: 'Negatif değerler (örn. −2) namazı birkaç dakika öne alır.', es: 'Los valores negativos (p. ej. −2) adelantan la oración unos minutos.', pt: 'Valores negativos (ex. −2) antecipam a oração em alguns minutos.', de: 'Negative Werte (z. B. −2) bringen das Gebet um einige Minuten nach vorne.', it: 'Valori negativi (es. −2) anticipano la preghiera di qualche minuto.' },
  confirmedAt:    { en: 'Performed at', ar: 'أُدّيت في', id: 'Ditunaikan pada', ur: 'ادا کی گئی', fr: 'Accomplie à', bn: 'আদায় করা হয়েছে', zh: '完成于', fa: 'ادا شده در', tr: 'Kılındı', es: 'Realizada a las', pt: 'Cumprida às', de: 'Verrichtet um', it: 'Compiuta alle' },
  tapWhenDone:    { en: 'Tap when done', ar: 'اضغط عند الأداء', id: 'Ketuk saat selesai', ur: 'ادائیگی پر دبائیں', fr: 'Appuyez après la prière', bn: 'আদায় করলে ট্যাপ', zh: '完成后点击', fa: 'پس از ادا لمس کنید', tr: 'Kılınca dokun', es: 'Toca al terminar', pt: 'Toque ao concluir', de: 'Nach Verrichtung antippen', it: 'Tocca quando fatto' },
};

// Calculation methods - multilingual labels
const METHODS = [
  { key: 'AUTO',
    labels: { en: 'Auto (based on location)', ar: 'تلقائي حسب الموقع', id: 'Otomatis (berdasarkan lokasi)', ur: 'خودکار (مقام کے مطابق)', fr: 'Auto (selon la position)', bn: 'অটো (অবস্থান অনুযায়ী)', zh: '自动（基于位置）', fa: 'خودکار (بر اساس موقعیت)', tr: 'Otomatik (konuma göre)' } },
  { key: 'MWL',
    labels: { en: 'Muslim World League', ar: 'رابطة العالم الإسلامي', id: 'Liga Dunia Muslim', ur: 'رابطۃ العالم الاسلامی', fr: 'Ligue Mondiale Islamique', bn: 'মুসলিম ওয়ার্ল্ড লিগ', zh: '世界穆斯林联盟', fa: 'اتحادیه جهان اسلام', tr: 'İslam Dünyası Birliği' } },
  { key: 'ISNA',
    labels: { en: 'ISNA (North America)', ar: 'الجمعية الإسلامية لأمريكا الشمالية', id: 'ISNA (Amerika Utara)', ur: 'ISNA (شمالی امریکہ)', fr: 'ISNA (Amérique du Nord)', bn: 'ISNA (উত্তর আমেরিকা)', zh: 'ISNA（北美）', fa: 'ISNA (آمریکای شمالی)', tr: 'ISNA (Kuzey Amerika)' } },
  { key: 'Egypt',
    labels: { en: 'Egyptian General Authority', ar: 'الهيئة المصرية العامة للمساحة', id: 'Otoritas Mesir', ur: 'مصری ادارہ', fr: 'Autorité Égyptienne', bn: 'মিসরীয় কর্তৃপক্ষ', zh: '埃及总署', fa: 'سازمان عمومی مصر', tr: 'Mısır Genel Otoritesi' } },
  { key: 'Karachi',
    labels: { en: 'Karachi', ar: 'جامعة كراتشي', id: 'Karachi', ur: 'جامعہ کراچی', fr: 'Karachi', bn: 'করাচি', zh: '卡拉奇', fa: 'دانشگاه کراچی', tr: 'Karaçi' } },
  { key: 'Makkah',
    labels: { en: 'Umm al-Qura (Saudi Arabia)', ar: 'أم القرى (السعودية)', id: 'Umm al-Qura (Saudi)', ur: 'ام القری (سعودی عرب)', fr: 'Umm al-Qura (Arabie Saoudite)', bn: 'উম্মুল কুরা (সৌদি আরব)', zh: '乌姆古拉（沙特）', fa: 'ام‌القری (عربستان)', tr: 'Ümmü\'l-Kurâ (Suudi Arabistan)' } },
];

const ASR_METHODS = [
  { key: 'Standard',
    labels: { en: 'Standard (Shafi/Maliki/Hanbali)', ar: 'الجمهور (شافعي/مالكي/حنبلي)', id: 'Standar (Syafi\'i/Maliki/Hanbali)', ur: 'جمہور (شافعی/مالکی/حنبلی)', fr: 'Standard (Shafi/Maliki/Hanbali)', bn: 'স্ট্যান্ডার্ড (শাফি/মালিকি/হাম্বলি)', zh: '通用（沙菲仪/马立克/罕百里）', fa: 'استاندارد (شافعی/مالکی/حنبلی)', tr: 'Standart (Şâfi/Mâliki/Hanbeli)' } },
  { key: 'Hanafi',
    labels: { en: 'Hanafi', ar: 'الحنفي', id: 'Hanafi', ur: 'حنفی', fr: 'Hanafi', bn: 'হানাফি', zh: '哈乃斐', fa: 'حنفی', tr: 'Hanefi' } },
];

// High-latitude adjustment notices (shown when backend reports it).
// Two variants: 'angle_based' (mild high-lat, coherent daylight) vs
// 'nearest_latitude' (polar day/night, times borrowed from 48° latitude).
const HIGH_LAT_NOTICE = {
  angle_based: {
    en: 'Your location is at a high latitude. In summer the Sun does not dip low enough for the standard Fajr/Isha depression angles, so we apply the industry-standard Angle-Based Method (fajr = last portion of the astronomical night, isha = first portion). Times are approximate — please consult your local mosque for the authoritative schedule.',
    ar: 'موقعك في خط عرض عالٍ. في الصيف لا تنخفض الشمس بما يكفي لبلوغ زاويتَي الفجر والعشاء الفلكيتين، لذا نطبّق طريقة "تقسيم الليل" المعتمدة عالمياً (الفجر = آخر جزء من الليل الفلكي، والعشاء = أوّله). الأوقات تقريبية — يُرجى مراجعة مسجدك المحلي لاعتماد الوقت الرسمي.',
    id: 'Lokasi Anda berada pada garis lintang tinggi. Di musim panas Matahari tidak turun cukup rendah untuk sudut Fajr/Isya standar, jadi kami menerapkan Metode Berbasis Sudut yang standar (fajar = bagian terakhir malam astronomi, isya = bagian awal). Waktu bersifat perkiraan — silakan konsultasikan masjid setempat untuk jadwal resmi.',
    ur: 'آپ کا مقام بلند عرض البلد پر ہے۔ گرمیوں میں سورج فجر/عشاء کے فلکی زاویوں کے لیے کافی نیچے نہیں آتا، لہٰذا ہم عالمی طور پر تسلیم شدہ "زاویہ پر مبنی طریقہ" استعمال کرتے ہیں (فجر = فلکی رات کا آخری حصہ، عشاء = ابتدائی حصہ)۔ اوقات تقریبی ہیں — مستند وقت کے لیے اپنی قریبی مسجد سے رجوع کریں۔',
    fr: "Votre position est à haute latitude. En été, le Soleil ne descend pas assez pour atteindre les angles de dépression standards du Fajr/Icha, nous appliquons donc la Méthode Basée sur l'Angle standard (Fajr = dernière portion de la nuit astronomique, Icha = première portion). Les heures sont approximatives — veuillez consulter votre mosquée locale pour l'horaire officiel.",
    bn: 'আপনার অবস্থান উচ্চ অক্ষাংশে। গ্রীষ্মে সূর্য ফজর/এশার আদর্শ কোণে পর্যন্ত নিচে নামে না, তাই আমরা আন্তর্জাতিকভাবে স্বীকৃত অ্যাঙ্গেল-ভিত্তিক পদ্ধতি প্রয়োগ করি (ফজর = জ্যোতির্বৈজ্ঞানিক রাতের শেষ অংশ, এশা = শুরুর অংশ)। সময় আনুমানিক — অনুমোদিত সময়ের জন্য আপনার স্থানীয় মসজিদে যোগাযোগ করুন।',
    zh: '您所在位置为高纬度地区。夏季太阳无法降至标准的晨礼/宵礼所需的角度，因此我们采用国际通用的"角度分割法"（晨礼 = 天文夜的最后一段，宵礼 = 天文夜的最初一段）。时间为估算 — 请以当地清真寺公布的时间为准。',
    fa: 'موقعیت شما در عرض جغرافیایی بالا قرار دارد. در تابستان خورشید به اندازه کافی برای زوایای استاندارد فجر/عشاء پایین نمی‌رود، بنابراین از روش استاندارد بین‌المللی «تقسیم شب» استفاده می‌کنیم (فجر = آخرین بخش شب نجومی، عشاء = ابتدای شب). زمان‌ها تقریبی هستند — لطفاً برای زمان دقیق به مسجد محلی خود مراجعه کنید.',
    tr: 'Konumunuz yüksek enlemde. Yazın Güneş, standart İmsak/Yatsı çukurluk açılarına ulaşacak kadar aşağı inmez; bu yüzden dünya çapında kabul gören Açıya-Dayalı Yöntemi uyguluyoruz (İmsak = astronomik gecenin son kısmı, Yatsı = ilk kısmı). Vakitler yaklaşıktır — kesin vakit için yerel caminizden teyit alın.',
  },
  nearest_latitude: {
    en: 'Your location is in the polar zone where the Sun does not set (or rise) at this time of year. We use the Nearest-Latitude Method: prayer times are borrowed from the same longitude at 48° latitude. Times are approximate — please consult your local mosque for the authoritative schedule.',
    ar: 'موقعك في المنطقة القطبية حيث لا تغيب الشمس (أو لا تشرق) في هذا الوقت من السنة. نستخدم طريقة "أقرب خط عرض": يتم استعارة مواقيت الصلاة من نفس خط الطول عند خط عرض 48°. الأوقات تقريبية — يُرجى مراجعة مسجدك المحلي لاعتماد الوقت الرسمي.',
    id: 'Lokasi Anda berada di zona kutub, di mana Matahari tidak terbenam (atau tidak terbit) pada saat ini dalam setahun. Kami menggunakan Metode Lintang Terdekat: waktu salat diambil dari bujur yang sama pada lintang 48°. Waktu bersifat perkiraan — silakan konsultasikan masjid setempat untuk jadwal resmi.',
    ur: 'آپ کا مقام قطبی خطے میں ہے جہاں سال کے اس وقت میں سورج غروب (یا طلوع) نہیں ہوتا۔ ہم "قریب ترین عرض" کا طریقہ استعمال کرتے ہیں: نمازوں کے اوقات اسی طول البلد پر 48° عرض سے لیے جاتے ہیں۔ اوقات تقریبی ہیں — مستند وقت کے لیے اپنی قریبی مسجد سے رجوع کریں۔',
    fr: "Votre position se trouve dans la zone polaire où le Soleil ne se couche pas (ou ne se lève pas) à cette période de l'année. Nous utilisons la Méthode de la Latitude la plus Proche : les heures de prière sont empruntées à la même longitude à 48° de latitude. Les heures sont approximatives — veuillez consulter votre mosquée locale pour l'horaire officiel.",
    bn: 'আপনার অবস্থান মেরু অঞ্চলে যেখানে বছরের এই সময়ে সূর্য অস্ত (বা উদয়) হয় না। আমরা "নিকটতম অক্ষাংশ" পদ্ধতি ব্যবহার করি: নামাজের সময় একই দ্রাঘিমাংশের 48° অক্ষাংশ থেকে ধার করা হয়। সময় আনুমানিক — অনুমোদিত সময়ের জন্য আপনার স্থানীয় মসজিদে যোগাযোগ করুন।',
    zh: '您所在位置处于极地区，在一年中的这个时段太阳不落（或不升）。我们采用"最近纬度法"：礼拜时间借用同一经度在纬度48°的对应时间。时间为估算 — 请以当地清真寺公布的时间为准。',
    fa: 'موقعیت شما در منطقه‌ی قطبی است که در این زمان از سال خورشید غروب (یا طلوع) نمی‌کند. از روش "نزدیک‌ترین عرض جغرافیایی" استفاده می‌کنیم: اوقات نماز از همان طول جغرافیایی در عرض ۴۸° وام گرفته می‌شود. زمان‌ها تقریبی هستند — لطفاً برای زمان دقیق به مسجد محلی خود مراجعه کنید.',
    tr: 'Konumunuz kutup bölgesindedir; yılın bu döneminde Güneş batmaz (veya doğmaz). En Yakın Enlem Yöntemi\'ni uyguluyoruz: namaz vakitleri aynı boylamda 48° enlemden ödünç alınır. Vakitler yaklaşıktır — kesin vakit için yerel caminizden teyit alın.',
  },
};

const HIGH_LAT_BADGE = {
  angle_based: {
    en: 'High-latitude estimate', ar: 'تقدير للعرض العالي', id: 'Estimasi lintang tinggi',
    ur: 'بلند عرض البلد کا تخمینہ', fr: 'Estimation haute latitude', bn: 'উচ্চ অক্ষাংশ অনুমান',
    zh: '高纬度估算', fa: 'برآورد عرض جغرافیایی بالا', tr: 'Yüksek enlem tahmini',
  },
  nearest_latitude: {
    en: 'Polar-zone estimate (Nearest-Latitude)', ar: 'تقدير قطبي (أقرب خط عرض)',
    id: 'Estimasi zona kutub (Lintang terdekat)', ur: 'قطبی تخمینہ (قریب ترین عرض)',
    fr: 'Estimation polaire (Latitude proche)', bn: 'মেরু অঞ্চল অনুমান (নিকটতম অক্ষাংশ)',
    zh: '极地估算（最近纬度）', fa: 'برآورد قطبی (نزدیک‌ترین عرض)',
    tr: 'Kutup bölgesi tahmini (En yakın enlem)',
  },
};

const PRAYER_META = {
  fajr:    { icon: Sunrise, color: '#7C3AED' },
  sunrise: { icon: Sun,     color: '#EA580C', isHelper: true },
  dhuhr:   { icon: Sun,     color: '#0284C7' },
  asr:     { icon: Sun,     color: '#C2410C' },
  maghrib: { icon: Sunset,  color: '#BE185D' },
  isha:    { icon: Star,    color: '#1E40AF' },
};

const PRAYER_ORDER = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

const SETTINGS_KEY = 'falak.prayer.settings.v1';

const DEFAULT_SETTINGS = {
  method: 'AUTO',
  asr_method: 'Standard',
  maghrib_offset_min: 0,
  fajr_offset_min: 0,
  isha_offset_min: 0,
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const PrayerTimesWidget = ({ location, timezone, selectedCity, surfaceColors, textColors, celestialStyle, sunPhase }) => {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const isRTL = RTL_LANGS.has(lang);
  const [prayerData, setPrayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [settingsOpen, setSettingsOpen] = useState(false);
  // ============================================================
  // Tamam al-Salah state — OWNED HERE at the parent level so that
  // remounts / re-renders of child components CANNOT lose it.
  //   • stage           : 'closed' | 'welcome' | 'session'
  //   • brightness      : 'dim' | 'bright' (v2 — reduced from 4 → 2)
  //   • rakatsDone      : 0 during welcome, seeded to TAMAM_INITIAL_RAKAT
  //                        when transitioning into session, incremented
  //                        by taps up to 4.
  //   • summaryCount    : number shown briefly in a toast after exit;
  //                        null when no toast should be visible.
  // ============================================================
  const [tamamStage, setTamamStage] = useState('closed');
  const [tamamBrightness, setTamamBrightness] = useState('dim');
  const [tamamRakats, setTamamRakats] = useState(0);
  const [tamamSummaryCount, setTamamSummaryCount] = useState(null);
  // Audio Notifications (Phase 1 — in-app only). The card is a modal
  // portal; the "has tone saved" flag drives the green dot on the
  // speaker-icon button. We refresh it when the card closes.
  const [audioOpen, setAudioOpen] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  useEffect(() => {
    (async () => {
      const s = getAudioSettings();
      const t = await loadAudioTone();
      setAudioEnabled(Boolean(s.enabled && t));
    })();
  }, [audioOpen]);
  const [settings, setSettings] = useState(loadSettings);
  // Hint is ALWAYS available. Default state is COLLAPSED — user expands on demand.
  // Not persisted: every fresh visit starts collapsed.
  const [hintExpanded, setHintExpanded] = useState(false);

  // Draft settings used inside the settings modal. The committed `settings`
  // only update when the user explicitly clicks "Save". The draft is reset to
  // the latest committed settings every time the modal opens.
  const [draftSettings, setDraftSettings] = useState(settings);
  const openSettings = () => {
    setDraftSettings(settings);
    setSettingsOpen(true);
  };
  const closeSettings = () => setSettingsOpen(false);
  const updateDraft = (k, v) => setDraftSettings(s => ({ ...s, [k]: v }));
  const resetDraftToGPS = () => setDraftSettings(DEFAULT_SETTINGS);
  const saveSettings = () => {
    setSettings(draftSettings);
    setSettingsOpen(false);
  };

  // Compute the effective method: when settings.method is 'AUTO', derive from
  // the selected city's country or, if no city, from raw GPS coords.
  const effectiveMethod = useMemo(() => {
    if (settings.method !== 'AUTO') return settings.method;
    return autoPickMethod({
      selectedCity,
      lat: location?.lat,
      lon: location?.lon,
    });
  }, [settings.method, selectedCity, location?.lat, location?.lon]);

  // Persist settings whenever they change
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); }
    catch (_) { /* localStorage may be unavailable in some browsers */ }
  }, [settings]);

  // Background prayer alerts (Phase 2 — Web Push). Schedule is re-sent
  // when location/settings change and at most once a day otherwise.
  const pushParams = useMemo(() => {
    if (!location) return null;
    const names = {};
    ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].forEach((p) => { names[p] = tt(PRAYER_NAMES[p], lang); });
    return {
      lat: location.lat,
      lon: location.lon,
      method: effectiveMethod,
      asr_method: settings.asr_method,
      fajr_offset_min: settings.fajr_offset_min,
      maghrib_offset_min: settings.maghrib_offset_min,
      isha_offset_min: settings.isha_offset_min,
      lang,
      titleTemplate: tt(AUDIO_STRINGS.notifTitle, lang),
      body: tt(TAMAM_STRINGS.entrySubtitle, lang),
      names,
    };
  }, [location, effectiveMethod, settings.asr_method, settings.fajr_offset_min,
      settings.maghrib_offset_min, settings.isha_offset_min, lang]);

  useEffect(() => {
    if (!pushParams) return undefined;
    const sync = () => { syncPushIfNeeded(pushParams).catch(() => {}); };
    sync();
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [pushParams]);

  // Push arriving while the app is on screen: the custom tone (if saved)
  // plays via the in-app tick; otherwise show the normal notification.
  useEffect(() => listenForegroundPush((msg) => {
    if (!getAudioSettings().enabled) showForegroundNotification(msg).catch(() => {});
  }), []);

  useEffect(() => {
    if (!location) return;
    const fetchPrayers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/prayer-times`, {
          params: {
            lat: location.lat,
            lon: location.lon,
            method: effectiveMethod,
            asr_method: settings.asr_method,
            maghrib_offset_min: settings.maghrib_offset_min,
            fajr_offset_min: settings.fajr_offset_min,
            isha_offset_min: settings.isha_offset_min,
          }
        });
        setPrayerData(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Prayer times fetch error:', err);
        setLoading(false);
      }
    };
    fetchPrayers();
    const interval = setInterval(fetchPrayers, 600000);
    return () => clearInterval(interval);
  }, [location, effectiveMethod, settings.asr_method,
      settings.maghrib_offset_min, settings.fajr_offset_min, settings.isha_offset_min]);

  // Countdown to next prayer
  useEffect(() => {
    if (!prayerData?.next_prayer_time) return;
    const update = () => {
      const now = new Date();
      const target = new Date(prayerData.next_prayer_time);
      const diff = target - now;
      if (diff <= 0) {
        setCountdown({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown({ hours, minutes, seconds });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [prayerData]);

  // Prayer performance confirmations.
  //
  // localStorage is our source of truth. We hold it in state so React
  // re-renders when it changes, and refresh it in three cases:
  //   1. Immediately after the user taps to confirm (handleConfirmPrayer).
  //   2. Periodically (every 30 s) — covers day rollover and stale reads.
  //   3. When the browser tab regains visibility — TWA/backgrounded apps
  //      often come back with stale React state, so we re-sync from LS.
  //
  // We DO NOT depend on `countdown` values because that memoisation
  // strategy caused a race: when the widget briefly rendered its loading
  // state after the 10-minute prayer-times refetch, previously-confirmed
  // cards could momentarily lose their confirmed styling and flash back
  // into the pulsing "eligible" state.
  const [confirmations, setConfirmations] = useState(() => getTodayConfirmations());

  useEffect(() => {
    const refresh = () => setConfirmations(getTodayConfirmations());
    const interval = setInterval(refresh, 30_000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Auto-hide the Tamam al-Salah summary toast ~4s after it appears.
  useEffect(() => {
    if (tamamSummaryCount == null) return undefined;
    const t = setTimeout(() => setTamamSummaryCount(null), 4000);
    return () => clearTimeout(t);
  }, [tamamSummaryCount]);

  // Audio Notifications auto-fire — Phase 1 (foreground only).
  //
  // Every 2 seconds we check whether a prayer time has just entered its
  // first 60-second window. If so, and the user has a saved tone and
  // hasn't been notified for this prayer today, we play the audio
  // (volume + repeat count honoured) and mark it as fired.
  //
  // If the app was closed when a prayer passed and we re-open "late"
  // (more than 60 s after it), we silently mark it fired to avoid a
  // jarring late replay. The user still sees the prayer card.
  useEffect(() => {
    if (!prayerData) return undefined;
    const CORE = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
    const WINDOW_MS = 60_000;
    let cached = null;
    const tick = async () => {
      const s = getAudioSettings();
      if (!s.enabled) return;
      const now = Date.now();
      for (const p of CORE) {
        const iso = prayerData[p];
        if (!iso) continue;
        if (hasPrayerAudioFired(p)) continue;
        const t = new Date(iso).getTime();
        if (Number.isNaN(t)) continue;
        if (now < t) continue; // not yet
        const diff = now - t;
        if (diff > WINDOW_MS) {
          // Too late — mark fired so we don't play stale audio next tick.
          markPrayerAudioFired(p);
          continue;
        }
        // Within the fire window — try to play.
        if (!cached) {
          const tone = await loadAudioTone();
          cached = tone;
        }
        if (cached && cached.blob) {
          playAudioBlob(cached.blob, { volume: s.volume, repeat: s.repeat });
        }
        markPrayerAudioFired(p);
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, [prayerData]);

  const handleConfirmPrayer = (prayer, prayerTimeIso) => {
    if (!canConfirm(prayer, prayerTimeIso)) return;
    confirmPrayer(prayer);
    setConfirmations(getTodayConfirmations());
  };

  if (loading || !prayerData) {
    return (
      <div
        className="p-6 md:p-8 rounded-2xl backdrop-blur-2xl border glass-surface"
        style={{ background: surfaceColors.background, borderColor: surfaceColors.border }}
        data-testid="prayer-times-widget"
      >
        <div style={{ color: textColors.secondary }}>{t('loading')}</div>
      </div>
    );
  }

  const formatTime = (isoStr) => {
    if (!isoStr) return '--:--';
    const d = new Date(isoStr);
    const opts = { hour: '2-digit', minute: '2-digit', hour12: true };
    if (timezone) opts.timeZone = timezone;
    const locale = lang === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn'
                : lang === 'ur' ? 'ur-PK-u-ca-gregory-nu-latn'
                : lang === 'fa' ? 'fa-IR-u-ca-gregory-nu-latn'
                : lang === 'tr' ? 'tr-TR'
                : lang === 'zh' ? 'zh-CN'
                : lang === 'fr' ? 'fr-FR'
                : lang === 'bn' ? 'bn-BD-u-nu-latn'
                : lang === 'id' ? 'id-ID'
                : 'en-US';
    return d.toLocaleTimeString(locale, opts);
  };

  const next = prayerData.next_prayer;
  const nextMeta = next ? PRAYER_META[next] : null;
  const nextLabel = next ? tt(PRAYER_NAMES[next], lang) : '';

  const methodLabel = (() => {
    const chosen = METHODS.find(x => x.key === settings.method);
    const chosenLabel = chosen ? tt(chosen.labels, lang) : settings.method;

    if (settings.method !== 'AUTO') return chosenLabel;

    const effective = METHODS.find(x => x.key === effectiveMethod);
    const effLabel = effective ? tt(effective.labels, lang) : effectiveMethod;
    return tt(UI.autoArrow, lang) + effLabel;
  })();
  const asrLabel = (() => {
    const m = ASR_METHODS.find(x => x.key === settings.asr_method);
    return m ? tt(m.labels, lang) : settings.asr_method;
  })();

  return (
    <div
      className="p-5 md:p-7 rounded-2xl backdrop-blur-2xl border glass-surface relative"
      style={{ background: surfaceColors.background, borderColor: surfaceColors.border }}
      data-testid="prayer-times-widget"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Moon size={22} style={{ color: textColors.accent }} />
          <h3 className="text-heading text-lg md:text-xl" style={{ color: textColors.primary }}>
            {tt(UI.prayerTimes, lang)}
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {next && (
            <div
              className="px-4 py-2 rounded-xl"
              style={{
                background: `${nextMeta.color}22`,
                border: `1px solid ${nextMeta.color}44`
              }}
              data-testid="next-prayer-countdown"
            >
              <div className="text-[10px] text-caption mb-0.5" style={{ color: textColors.secondary }}>
                {tt(UI.nextPrayer, lang)}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold" style={{ color: textColors.primary }}>
                  {nextLabel}
                </span>
                <span className="tabular-nums mono-text text-sm" style={{ color: nextMeta.color }}>
                  {String(countdown.hours).padStart(2, '0')}:
                  {String(countdown.minutes).padStart(2, '0')}:
                  {String(countdown.seconds).padStart(2, '0')}
                </span>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={openSettings}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full transition-all hover:bg-white/10"
            style={{ color: textColors.secondary, border: `1px solid ${surfaceColors.border}` }}
            data-testid="prayer-settings-open"
            aria-label={tt(UI.prayerSettings, lang)}
            title={tt(UI.settings, lang)}
          >
            <Settings size={16} />
            <span className="text-xs font-medium">{tt(UI.settings, lang)}</span>
          </button>
        </div>
      </div>

      {/* Onboarding hint banner — always visible, collapsible per session */}
      {(() => {
        const hintLang = HINT[lang] ? lang : 'en';
        const h = HINT[hintLang];
        return (
          <div
            className="mb-5 rounded-xl border overflow-hidden"
            style={{
              background: 'rgba(14, 165, 233, 0.08)',
              borderColor: 'rgba(14, 165, 233, 0.35)',
            }}
            data-testid="prayer-hint-banner"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <button
              type="button"
              onClick={() => setHintExpanded(e => !e)}
              className="w-full p-3 md:p-4 flex items-center gap-3 transition-colors hover:bg-white/5"
              data-testid="prayer-hint-toggle"
              aria-expanded={hintExpanded}
            >
              <div
                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(14, 165, 233, 0.18)' }}
              >
                <MapPin size={16} style={{ color: '#0EA5E9' }} />
              </div>
              <div className="flex-1 min-w-0 text-start">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: textColors.primary }}>
                    {h.title}
                  </span>
                  <Info size={13} style={{ color: 'rgba(14, 165, 233, 0.8)' }} />
                </div>
                {!hintExpanded && (
                  <span className="text-[11.5px] mt-0.5 block" style={{ color: textColors.secondary }}>
                    {h.expand}
                  </span>
                )}
              </div>
              {hintExpanded
                ? <ChevronUp   size={18} style={{ color: 'rgba(14, 165, 233, 0.8)', flexShrink: 0 }} />
                : <ChevronDown size={18} style={{ color: 'rgba(14, 165, 233, 0.8)', flexShrink: 0 }} />}
            </button>

            {hintExpanded && (
              <div className="px-4 pb-4 ps-[68px]" data-testid="prayer-hint-details">
                <p
                  className="text-[12.5px] leading-relaxed mb-1"
                  style={{ color: textColors.secondary }}
                  data-testid="prayer-hint-p1"
                >
                  {h.p1}
                </p>
                <p
                  className="text-[12.5px] leading-relaxed mb-1.5"
                  style={{ color: textColors.secondary }}
                  data-testid="prayer-hint-p2"
                >
                  {h.p2}
                </p>
                <ul
                  className="text-[12px] leading-relaxed space-y-0.5 list-disc"
                  style={{ color: textColors.secondary, paddingInlineStart: '1.25rem' }}
                >
                  <li>{h.li1}</li>
                  <li>{h.li2}</li>
                  <li>{h.li3}</li>
                </ul>
              </div>
            )}
          </div>
        );
      })()}

      {/* High-latitude adjustment notice (shown only when backend reports it) */}
      {(prayerData.high_latitude_adjustment === 'angle_based' ||
        prayerData.high_latitude_adjustment === 'nearest_latitude') && (() => {
        const mode = prayerData.high_latitude_adjustment;
        const badgeText = (HIGH_LAT_BADGE[mode] || {})[lang] || HIGH_LAT_BADGE[mode]?.en || '';
        const noticeText = (HIGH_LAT_NOTICE[mode] || {})[lang] || HIGH_LAT_NOTICE[mode]?.en || '';
        return (
          <div
            className="mb-3 md:mb-4 p-3 rounded-lg text-xs"
            style={{
              background: 'rgba(234, 179, 8, 0.10)',
              border: '1px solid rgba(234, 179, 8, 0.35)',
              color: '#FCD34D'
            }}
            data-testid="high-latitude-notice"
            data-mode={mode}
          >
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 mt-0.5" style={{ fontSize: 14 }}>⚠️</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold mb-1 flex items-center gap-2 flex-wrap">
                  <span>{badgeText}</span>
                </div>
                <p className="leading-relaxed" style={{ color: 'rgba(252, 211, 77, 0.85)' }}>
                  {noticeText}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Prayer Times Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 lg:gap-4">
        {PRAYER_ORDER.map(prayer => {
          const meta = PRAYER_META[prayer];
          const Icon = meta.icon;
          const isNext = prayer === next;
          const isHelper = meta.isHelper === true; // sunrise is not a prayer to confirm
          const time = prayerData[prayer];
          const label = tt(PRAYER_NAMES[prayer], lang);
          const confirmedIso = !isHelper ? confirmations[prayer] : null;
          const isConfirmed = Boolean(confirmedIso);
          const eligible = !isHelper && !isConfirmed && canConfirm(prayer, time);

          // Card background rules:
          //   • Confirmed → transparent green fill
          //   • Eligible (past prayer time + delay, not yet confirmed) → an
          //     animated green pulse (see .prayer-eligible-pulse in index.css)
          //     which replaces any static background.
          //   • Next prayer → transparent background, only the border shows
          //   • Anything else → the subtle default light-white
          const bg = isConfirmed
            ? 'rgba(16, 185, 129, 0.18)'
            : eligible
              ? undefined // handled by the CSS class animation
              : isNext
                ? 'transparent'
                : 'rgba(255, 255, 255, 0.05)';

          const border = isConfirmed
            ? '1px solid rgba(16, 185, 129, 0.55)'
            : eligible
              ? '1px solid rgba(16, 185, 129, 0.35)'
              : isNext
                ? `1px solid ${meta.color}`
                : '1px solid transparent';

          const clickable = eligible;
          const CardTag = clickable ? 'button' : 'div';

          return (
            <CardTag
              key={prayer}
              type={clickable ? 'button' : undefined}
              onClick={clickable ? () => handleConfirmPrayer(prayer, time) : undefined}
              className={`p-3 lg:p-4 rounded-xl transition-all ${clickable ? 'hover:brightness-110 active:scale-[0.98] cursor-pointer' : ''} ${eligible ? 'prayer-eligible-pulse' : ''}`}
              style={{
                background: bg,
                border,
                textAlign: 'inherit',
                width: '100%',
                display: 'block',
                appearance: 'none',
              }}
              data-testid={`prayer-${prayer}`}
              data-confirmed={isConfirmed ? 'true' : 'false'}
              data-eligible={eligible ? 'true' : 'false'}
              aria-label={clickable ? `${label} — ${tt(UI.tapWhenDone, lang)}` : undefined}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Icon size={14} style={{ color: meta.color }} />
                <span
                  className="text-caption"
                  style={{ color: textColors.secondary, fontSize: 'clamp(0.75rem, 0.95vw, 1.05rem)' }}
                >
                  {label}
                </span>
                {isConfirmed && (
                  <Check
                    size={14}
                    style={{ color: '#10B981', marginInlineStart: 'auto' }}
                    data-testid={`prayer-${prayer}-check`}
                  />
                )}
              </div>
              <div
                className="tabular-nums mono-text font-medium"
                style={{ color: textColors.primary, fontSize: 'clamp(1.125rem, 1.7vw, 1.9rem)' }}
              >
                {formatTime(time)}
              </div>
              {isConfirmed && (
                <div
                  className="text-[10.5px] mt-1 tabular-nums"
                  style={{ color: '#10B981', opacity: 0.9 }}
                  data-testid={`prayer-${prayer}-confirmed-at`}
                >
                  {tt(UI.confirmedAt, lang)} {formatTime(confirmedIso)}
                </div>
              )}
            </CardTag>
          );
        })}
      </div>

      {/* ===== Tamam al-Salah + Audio Notifications — split button =====
           Phase 1 of Audio Notifications: a small square button cut from
           the main Tamam entry opens a settings card. A green dot
           appears when the user has saved a tone (audioEnabled). The
           main button keeps 100% of its original look, just shortened. */}
      <div className="mt-5 flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => {
            // Reset rakat count so a stale value can't leak between sessions.
            setTamamRakats(0);
            setTamamStage('welcome');
          }}
          className="flex-1 rounded-xl p-3.5 flex items-center gap-3 transition-all hover:brightness-110 active:scale-[0.99] relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${textColors.accent}22, ${textColors.accent}11)`,
            border: `1.5px solid ${textColors.accent}66`,
            boxShadow: `0 0 20px ${textColors.accent}18`,
            minWidth: 0,
          }}
          data-testid="tamam-entry-button"
          aria-label={tt(TAMAM_STRINGS.entryTitle, lang)}
        >
          {/* "NEW" corner ribbon — shown for 30 days after launch.
              Positioned in the TOP-LEFT corner as requested (works for
              both RTL and LTR — visually the ribbon feels like a badge). */}
          {isTamamNewBadgeVisible() && (
            <div
              className="absolute pointer-events-none"
              style={{
                top: 10,
                left: -28,
                transform: 'rotate(-45deg)',
                transformOrigin: 'center',
                background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                color: '#FFFFFF',
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.5px',
                padding: '2px 30px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                zIndex: 2,
              }}
              data-testid="tamam-new-ribbon"
            >
              {tt(TAMAM_STRINGS.newRibbon, lang)}
            </div>
          )}

          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `${textColors.accent}33` }}
          >
            <Moon size={20} color={textColors.accent} />
          </div>
          <div className="flex-1 min-w-0" style={{ textAlign: isRTL ? 'right' : 'left' }}>
            <div
              className="text-heading text-sm md:text-base"
              style={{
                color: textColors.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tt(TAMAM_STRINGS.entryTitle, lang)}
            </div>
            <div
              className="text-[11px] md:text-xs"
              style={{
                color: textColors.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tt(TAMAM_STRINGS.entrySubtitle, lang)}
            </div>
          </div>
        </button>

        {/* Audio Notifications — compact square (speaker icon only). */}
        <button
          type="button"
          onClick={() => setAudioOpen(true)}
          className="rounded-xl flex items-center justify-center flex-shrink-0 relative transition-all hover:brightness-110 active:scale-[0.98]"
          style={{
            width: 56,
            background: audioEnabled
              ? `linear-gradient(135deg, ${textColors.accent}44, ${textColors.accent}22)`
              : 'rgba(255,255,255,0.06)',
            border: audioEnabled
              ? `1.5px solid ${textColors.accent}aa`
              : `1.5px solid ${surfaceColors.border}`,
            boxShadow: audioEnabled ? `0 0 20px ${textColors.accent}30` : 'none',
          }}
          aria-label={tt(AUDIO_STRINGS.entryAria, lang)}
          data-testid="audio-notifications-entry"
        >
          <Volume2
            size={22}
            color={audioEnabled ? textColors.accent : textColors.primary}
            strokeWidth={audioEnabled ? 2.5 : 2}
          />
          {audioEnabled && (
            <span
              className="absolute rounded-full"
              style={{
                top: 6,
                right: 6,
                width: 7,
                height: 7,
                background: '#10B981',
                boxShadow: '0 0 6px rgba(16,185,129,0.8)',
              }}
              data-testid="audio-active-dot"
            />
          )}
        </button>
      </div>

      {/* Audio Notifications — settings card (portal modal) */}
      <AudioNotificationsCard
        open={audioOpen}
        onClose={() => setAudioOpen(false)}
        lang={lang}
        isRTL={isRTL}
        pushParams={pushParams}
      />


      {/* ===== Tamam al-Salah — controlled from parent state =====
          The parent (this widget) owns the state machine. Even if
          the child components remount for any reason, the stage and
          rakat count are preserved here, so the user can never be
          silently kicked back to the welcome screen mid-prayer. */}
      {tamamStage === 'welcome' && (
        <TamamWelcome
          lang={lang}
          onStart={(brightness) => {
            setTamamBrightness(brightness);
            // Seed the session with the first rakat already lit — the
            // moment the user starts, they ARE in rakat 1 (not "before it").
            setTamamRakats(TAMAM_INITIAL_RAKAT);
            setTamamStage('session');
          }}
          onCancel={() => setTamamStage('closed')}
        />
      )}
      {tamamStage === 'session' && (
        <TamamSession
          lang={lang}
          brightness={tamamBrightness}
          rakatsDone={tamamRakats}
          onIncrement={() => setTamamRakats((n) => Math.min(4, n + 1))}
          onExit={(count) => {
            if (count > 0) setTamamSummaryCount(count);
            setTamamStage('closed');
          }}
        />
      )}
      <TamamSummaryToast count={tamamSummaryCount} lang={lang} />

      {/* Method info footer */}
      <div
        className="mt-4 pt-3 border-t text-[10px] text-caption flex items-center justify-between flex-wrap gap-2"
        style={{ borderColor: surfaceColors.border, color: textColors.secondary }}
      >
        <span data-testid="prayer-method-label">
          {tt(UI.methodLabel, lang) + methodLabel}
        </span>
      </div>

      {/* ====== Settings Modal ====== */}
      {settingsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
          onClick={closeSettings}
          data-testid="prayer-settings-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border overflow-hidden"
            style={{
              background: '#0B132B',
              borderColor: 'rgba(255,255,255,0.2)',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 24px 64px rgba(0,0,0,0.7)'
            }}
            onClick={(e) => e.stopPropagation()}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-3">
                <Settings size={20} style={{ color: '#0EA5E9' }} />
                <h3 className="text-lg font-semibold text-white">
                  {tt(UI.prayerSettingsTitle, lang)}
                </h3>
              </div>
              <button
                onClick={closeSettings}
                className="text-white/60 hover:text-white p-1"
                data-testid="prayer-settings-close"
                aria-label={tt(UI.close, lang)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {tt(UI.modalIntro, lang)}
              </p>

              {/* Method */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>
                  {tt(UI.calcMethod, lang)}
                </label>
                <select
                  value={draftSettings.method}
                  onChange={(e) => updateDraft('method', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border focus:border-blue-400/60 text-white"
                  style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
                  data-testid="prayer-method-select"
                >
                  {METHODS.map(m => (
                    <option key={m.key} value={m.key} style={{ background: '#0B132B' }}>
                      {tt(m.labels, lang)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Asr */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#CBD5E1' }}>
                  {tt(UI.asrCalc, lang)}
                </label>
                <select
                  value={draftSettings.asr_method}
                  onChange={(e) => updateDraft('asr_method', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border focus:border-blue-400/60 text-white"
                  style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
                  data-testid="prayer-asr-select"
                >
                  {ASR_METHODS.map(m => (
                    <option key={m.key} value={m.key} style={{ background: '#0B132B' }}>
                      {tt(m.labels, lang)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Offsets */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'fajr_offset_min',    prayerKey: 'fajr' },
                  { key: 'maghrib_offset_min', prayerKey: 'maghrib' },
                  { key: 'isha_offset_min',    prayerKey: 'isha' },
                ].map(o => {
                  const prayerName = tt(PRAYER_NAMES[o.prayerKey], lang);
                  const offsetText = isRTL ? `${tt(UI.offset, lang)}${prayerName}` : `${prayerName}${tt(UI.offset, lang)}`;
                  return (
                  <div key={o.key}>
                    <label className="block text-[11px] font-medium mb-1.5" style={{ color: '#CBD5E1' }}>
                      {offsetText}
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={-30}
                        max={30}
                        value={draftSettings[o.key]}
                        onChange={(e) => updateDraft(o.key, parseInt(e.target.value || '0', 10))}
                        className="w-full px-2 py-2 rounded-lg text-sm outline-none border focus:border-blue-400/60 text-white text-center tabular-nums"
                        style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}
                        data-testid={`prayer-offset-${o.key}`}
                      />
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {tt(UI.minShort, lang)}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {tt(UI.negHint, lang)}
              </div>

              <div className="flex flex-col gap-2 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <button
                  onClick={saveSettings}
                  className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                  style={{ background: '#0EA5E9', color: '#FFFFFF' }}
                  data-testid="prayer-settings-save"
                >
                  {tt(UI.saveNew, lang)}
                </button>

                <button
                  onClick={resetDraftToGPS}
                  className="w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    background: 'rgba(16, 185, 129, 0.12)',
                    color: '#10B981',
                    border: '1px solid rgba(16, 185, 129, 0.35)'
                  }}
                  data-testid="prayer-settings-reset"
                >
                  {tt(UI.resetGPS, lang)}
                </button>

                <button
                  onClick={closeSettings}
                  className="w-full px-3 py-2 rounded-lg text-xs"
                  style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid="prayer-settings-cancel"
                >
                  {tt(UI.cancel, lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrayerTimesWidget;
